<script setup lang="ts">
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
</script>

<template>
  <!-- User Management Section -->
  <div id="user-management-section">
    <!-- Add User Form -->
    <div
      id="add-user-form"
      class="bg-white p-5 rounded-md border border-light-gray mb-6"
    >
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-dark-navy">{{ $t('government.administration.userManagement.form.title') }}</h3>
        <button
          class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700"
        >
          <i class="fa-solid fa-plus mr-2"></i>{{ $t('government.administration.userManagement.form.buttons.addUser') }}
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.organizationName') }}</label
          >
          <input
            type="text"
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
            :placeholder="$t('government.administration.userManagement.form.placeholders.organizationName')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.address') }}</label
          >
          <input
            type="text"
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
            :placeholder="$t('government.administration.userManagement.form.placeholders.address')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.contactNumber') }}</label
          >
          <input
            type="tel"
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
            :placeholder="$t('government.administration.userManagement.form.placeholders.contactNumber')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.pointOfContactName') }}</label
          >
          <input
            type="text"
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
            :placeholder="$t('government.administration.userManagement.form.placeholders.pointOfContactName')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.role') }}</label
          >
          <select
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
          >
            <option>{{ $t('government.administration.userManagement.roles.selectRole') }}</option>
            <option>{{ $t('government.administration.userManagement.roles.admin') }}</option>
            <option>{{ $t('government.administration.userManagement.roles.reviewer') }}</option>
            <option>{{ $t('government.administration.userManagement.roles.operator') }}</option>
            <option>{{ $t('government.administration.userManagement.roles.viewer') }}</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.contactEmail') }}</label
          >
          <input
            type="email"
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
            :placeholder="$t('government.administration.userManagement.form.placeholders.contactEmail')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.phoneNumber') }}</label
          >
          <input
            type="tel"
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
            :placeholder="$t('government.administration.userManagement.form.placeholders.phoneNumber')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.username') }}</label
          >
          <input
            type="text"
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
            :placeholder="$t('government.administration.userManagement.form.placeholders.username')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2"
            >{{ $t('government.administration.userManagement.form.labels.password') }}</label
          >
          <input
            type="password"
            class="w-full px-3 py-2 border border-light-gray rounded-md focus:ring-forest-green focus:border-forest-green"
            :placeholder="$t('government.administration.userManagement.form.placeholders.password')"
          />
        </div>
      </div>
    </div>

    <!-- User List -->
    <div
      id="user-list"
      class="bg-white rounded-md border border-light-gray"
    >
      <div class="p-5 border-b border-light-gray">
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-medium text-dark-navy">{{ $t('government.administration.userManagement.userList.title') }}</h3>
          <div class="flex space-x-3">
            <div class="relative">
              <input
                type="text"
:placeholder="$t('government.administration.userManagement.userList.search.placeholder')"
                class="pl-10 pr-4 py-2 border border-light-gray rounded-md"
              />
              <i
                class="fa-solid fa-search absolute left-3 top-3 text-gray-400"
              ></i>
            </div>
            <select class="px-3 py-2 border border-light-gray rounded-md">
              <option>{{ $t('government.administration.userManagement.userList.search.allRoles') }}</option>
              <option>{{ $t('government.administration.userManagement.roles.admin') }}</option>
              <option>{{ $t('government.administration.userManagement.roles.reviewer') }}</option>
              <option>{{ $t('government.administration.userManagement.roles.operator') }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {{ $t('government.administration.userManagement.userList.table.headers.organization') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {{ $t('government.administration.userManagement.userList.table.headers.contactPerson') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {{ $t('government.administration.userManagement.userList.table.headers.role') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {{ $t('government.administration.userManagement.userList.table.headers.email') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {{ $t('government.administration.userManagement.userList.table.headers.status') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {{ $t('government.administration.userManagement.userList.table.headers.actions') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr>
              <td class="px-6 py-4">
                <div>
                  <div class="font-medium text-gray-900">
                    Ministry of Environment
                  </div>
                  <div class="text-sm text-gray-500">
                    123 Government St, Capital City
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div>
                  <div class="font-medium text-gray-900">John Smith</div>
                  <div class="text-sm text-gray-500">+263-123-456789</div>
                </div>
              </td>
              <td class="px-6 py-4">
                <span
                  class="px-2 py-1 text-xs font-medium bg-forest-green text-white rounded-full"
                  >{{ $t('government.administration.userManagement.roles.admin') }}</span
                >
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">
                john.smith@env.gov.zw
              </td>
              <td class="px-6 py-4">
                <span
                  class="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                  >{{ $t('government.administration.userManagement.status.active') }}</span
                >
              </td>
              <td class="px-6 py-4">
                <div class="flex space-x-2">
                  <button
                    class="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button
                    class="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                  >
                    <i class="fa-solid fa-pause"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr>
              <td class="px-6 py-4">
                <div>
                  <div class="font-medium text-gray-900">
                    Carbon Registry Office
                  </div>
                  <div class="text-sm text-gray-500">
                    456 Registry Ave, Business District
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div>
                  <div class="font-medium text-gray-900">
                    Sarah Johnson
                  </div>
                  <div class="text-sm text-gray-500">+263-987-654321</div>
                </div>
              </td>
              <td class="px-6 py-4">
                <span
                  class="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                  >{{ $t('government.administration.userManagement.roles.reviewer') }}</span
                >
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">
                sarah.j@registry.gov.zw
              </td>
              <td class="px-6 py-4">
                <span
                  class="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                  >{{ $t('government.administration.userManagement.status.active') }}</span
                >
              </td>
              <td class="px-6 py-4">
                <div class="flex space-x-2">
                  <button
                    class="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button
                    class="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                  >
                    <i class="fa-solid fa-pause"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr>
              <td class="px-6 py-4">
                <div>
                  <div class="font-medium text-gray-900">
                    Energy Commission
                  </div>
                  <div class="text-sm text-gray-500">
                    789 Energy Blvd, Industrial Zone
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div>
                  <div class="font-medium text-gray-900">
                    Michael Brown
                  </div>
                  <div class="text-sm text-gray-500">+263-555-123456</div>
                </div>
              </td>
              <td class="px-6 py-4">
                <span
                  class="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full"
                  >{{ $t('government.administration.userManagement.roles.operator') }}</span
                >
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">
                m.brown@energy.gov.zw
              </td>
              <td class="px-6 py-4">
                <span
                  class="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full"
                  >{{ $t('government.administration.userManagement.status.inactive') }}</span
                >
              </td>
              <td class="px-6 py-4">
                <div class="flex space-x-2">
                  <button
                    class="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button
                    class="p-2 text-green-600 hover:bg-green-50 rounded"
                  >
                    <i class="fa-solid fa-play"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="px-6 py-3 border-t border-light-gray">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-500">
            {{ $t('government.administration.userManagement.userList.pagination.showing') }} 1 {{ $t('government.administration.userManagement.userList.pagination.to') }} 3 {{ $t('government.administration.userManagement.userList.pagination.of') }} 15 {{ $t('government.administration.userManagement.userList.pagination.results') }}
          </div>
          <div class="flex space-x-2">
            <button
              class="px-3 py-1 border border-light-gray rounded text-sm hover:bg-gray-50"
            >
              {{ $t('government.administration.userManagement.userList.pagination.previous') }}
            </button>
            <button
              class="px-3 py-1 bg-forest-green text-white rounded text-sm"
            >
              1
            </button>
            <button
              class="px-3 py-1 border border-light-gray rounded text-sm hover:bg-gray-50"
            >
              2
            </button>
            <button
              class="px-3 py-1 border border-light-gray rounded text-sm hover:bg-gray-50"
            >
              {{ $t('government.administration.userManagement.userList.pagination.next') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>